import React, { ReactNode } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Toaster } from '@/components/ui/toaster'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { ErrorNotificationProvider } from '@/components/ErrorNotificationProvider'
import { TranslationProvider } from '@/contexts/TranslationContext'
import { TimezoneDisplayProvider } from '@/contexts/TimezoneDisplayContext'
import { PresenceProvider, usePresence } from '@/contexts/PresenceContext'
import { GlobalChatNotifications } from '@/components/notifications/GlobalChatNotifications'
import { ScanStatusNotifications } from '@/components/notifications/ScanStatusNotifications'
import { useUser } from '@/app/store/auth'
import { VideoCallProvider, useVideoCall } from '@/features/ai-chat/core/components/video/VideoCallProvider'
import IncomingCallModal from '@/features/ai-chat/core/components/video/IncomingCallModal'
import App from './app/router'
import './styles/globals.css'
import './i18n'

// Initialize theme system immediately (before React renders)
// This prevents FOUC (Flash of Unstyled Content)
const initializeTheme = () => {
    const storedTheme = localStorage.getItem('ui-storage')

    if (storedTheme) {
        try {
            const parsed = JSON.parse(storedTheme)
            const theme = parsed.state?.theme || 'light'

            const root = document.documentElement
            root.classList.remove('dark', 'enhanced-light', 'enhanced-dark')

            if (theme === 'system') {
                const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
                if (prefersDark) {
                    root.classList.add('dark')
                }
            } else if (theme === 'dark' || theme === 'enhanced-dark') {
                root.classList.add('dark')
            }

            if (theme === 'enhanced-light' || theme === 'enhanced-dark') {
                root.classList.add(theme)
                root.setAttribute('data-theme', theme)
            }

            // CSS automatically applies all variables
        } catch (e) {
            console.error('Error parsing theme, fallback to light:', e)
            // Fallback to light theme
            const root = document.documentElement
            root.classList.remove('dark', 'enhanced-light', 'enhanced-dark')
        }
    } else {
        // Default to light theme
        const root = document.documentElement
        root.classList.remove('dark', 'enhanced-light', 'enhanced-dark')
    }
}

// Apply theme before React renders to avoid FOUC
initializeTheme()

// Initialize MSW in development
// Temporarily disabled due to cookie parsing issues
// if (import.meta.env.DEV) {
//     import('./test/mocks/server')
// }

// Create a client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes
            retry: (failureCount, error: any) => {
                if (error?.response?.status === 401 || error?.response?.status === 403) {
                    return false
                }
                return failureCount < 3
            },
        },
    },
})

/**
 * Incoming-call ring and call-failure surface.
 *
 * Rendered inside the provider so it can read the call state. `error` is the
 * only signal the engineer ever gets for a call that failed to start, was
 * declined, or was ended by the other side, so it has to be visible from
 * wherever they are — nothing rendered it before.
 *
 * This banner is THE surface for callState.error, and the only one: VideoCallModal
 * deliberately does not render it (see the note at VideoCallModal.jsx) and is
 * mounted nowhere, and the popup's own error screen is a different, popup-local
 * failure. One call failure, one report on screen — do not add a second.
 */
function GlobalCallUi() {
    const { t } = useTranslation()
    const { error, clearError } = useVideoCall()

    return (
        <>
            <IncomingCallModal onAccept={() => {}} onReject={() => {}} />
            {error && (
                <div
                    role="alert"
                    style={{
                        position: 'fixed',
                        top: '20px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 9998,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        maxWidth: '90vw',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        background: '#b91c1c',
                        color: '#fff',
                        fontSize: '14px',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
                    }}
                >
                    <span>{error}</span>
                    <button
                        type="button"
                        onClick={clearError}
                        aria-label={t('aiChat.modals.close')}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'inherit',
                            cursor: 'pointer',
                            fontSize: '18px',
                            lineHeight: 1,
                        }}
                    >
                        &times;
                    </button>
                </div>
            )}
        </>
    )
}

/**
 * App-wide video/audio call layer.
 *
 * `call-incoming` is delivered to the per-user socket room, but the only
 * listener lived inside ChatApp — which AiChatPanel unmounts whenever the panel
 * is closed (the default state), so a call to anyone who wasn't already sitting
 * in the chat panel was delivered and dropped. Mounting the provider above the
 * panel makes calls ring wherever the engineer is, and keeps the call popup's
 * signalling socket alive when the panel is closed mid-call.
 *
 * It rides on the always-connected PresenceContext socket, which the server has
 * already put in `user-<id>`.
 */
function GlobalVideoCall({ children }: { children: ReactNode }) {
    const { socket } = usePresence()
    const user = useUser()

    return (
        <VideoCallProvider socket={socket} userId={user?.id} userName={user?.name}>
            {children}
            <GlobalCallUi />
        </VideoCallProvider>
    )
}

// Disable StrictMode in development to prevent double-renders that cause lag
// StrictMode intentionally double-renders to help find bugs, but it makes hover feel slow
const AppWrapper = (
    <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
            <TranslationProvider>
                <TimezoneDisplayProvider>
                <PresenceProvider>
                    <GlobalVideoCall>
                    <BrowserRouter
                        future={{
                            v7_startTransition: true,
                            v7_relativeSplatPath: true
                        }}
                    >
                        <ErrorNotificationProvider>
                            <App />
                        </ErrorNotificationProvider>
                        <GlobalChatNotifications />
                        <ScanStatusNotifications />
                        <Toaster />
                    </BrowserRouter>
                    </GlobalVideoCall>
                </PresenceProvider>
                </TimezoneDisplayProvider>
            </TranslationProvider>
        </QueryClientProvider>
    </ErrorBoundary>
)

ReactDOM.createRoot(document.getElementById('root')!).render(
    import.meta.env.PROD ? <React.StrictMode>{AppWrapper}</React.StrictMode> : AppWrapper
)
